// Main Messaging Page
// Following existing patterns from Dashboard.tsx

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../../providers';
import { useMessaging } from '../../hooks';
import { MessagingLayout } from '../../components/Messaging/MessagingLayout';
import { ConversationList } from '../../components/Messaging/ConversationList';
import { BetDetail } from '../../components/Messaging/BetDetail';
import { MatchRequest } from '../../types/swipe';
import { ChatInterface } from '../../components/Messaging/ChatInterface';
import {
  MessagingLoading,
  MessagingError,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription
} from '../../styles/Messaging';
import { logger } from '../../utils/logger';

export const Messaging: React.FC = () => {
  const { user } = useAuthContext();
  const location = useLocation();
  const currentUserId = user?.id || '';
  const {
    conversations,
    currentConversation,
    isLoading,
    error,
    selectConversation,
    sendMessage,
    refreshConversations
  } = useMessaging(currentUserId);

  const [isMobile, setIsMobile] = useState(false);
  // A pending Message Bet opened from the list. Mutually exclusive with a
  // conversation: both occupy the same pane, and showing neither-or-both is
  // how you end up with a blank screen or two things fighting for it.
  const [selectedBet, setSelectedBet] = useState<MatchRequest | null>(null);
  // A conversation to open as soon as it appears in the list. Calling a bet
  // creates the conversation server-side, but this client's list is a moment
  // behind — so the id is held here and the effect below opens it when the
  // refresh lands.
  const [conversationToOpen, setConversationToOpen] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationToOpen) return;
    const match = conversations.find((c) => c.id === conversationToOpen);
    if (match) {
      selectConversation(match);
      setConversationToOpen(null);
    }
  }, [conversationToOpen, conversations, selectConversation]);

  // Deep link: when arrived here from "Message" on a profile, open that
  // conversation once it appears in the loaded list.
  const requestedConversationId = (location.state as { conversationId?: string } | null)?.conversationId;
  useEffect(() => {
    if (!requestedConversationId || currentConversation) return;
    const match = conversations.find((c) => c.id === requestedConversationId);
    if (match) selectConversation(match);
  }, [requestedConversationId, conversations, currentConversation, selectConversation]);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleConversationSelect = (conversation: any) => {
    selectConversation(conversation);
  };

  const handleSendMessage = async (content: string, attachments?: any[]) => {
    // Rethrown rather than swallowed: ChatInterface is what renders the
    // reason, and it only ever saw success because this caught first. A send
    // refused by the paywall looked like a message that simply vanished.
    await sendMessage(content, attachments);
  };

  const handleSearch = (query: string) => {
    // Future: Implement search functionality
    logger.log('Searching conversations:', query);
  };

  const handleFilter = (filter: string) => {
    // Future: Implement filter functionality
    logger.log('Filtering conversations:', filter);
  };

  if (isLoading) {
    return (
      <MessagingLoading>
        Loading conversations...
      </MessagingLoading>
    );
  }

  // Only a failure to LOAD the conversation list reaches here now — a rejected
  // send no longer sets this, so it cannot replace the whole page. Even so it
  // offers a way out, because a dead end with no button is not an error
  // screen, it is a trap.
  if (error) {
    return (
      <MessagingError>
        <div>We couldn't load your messages</div>
        <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>{error}</div>
        <button
          type="button"
          onClick={() => { refreshConversations(); }}
          style={{
            marginTop: '1rem',
            padding: '0.6rem 1.2rem',
            borderRadius: '999px',
            border: '1px solid var(--border-medium)',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </MessagingError>
    );
  }


  return (
    <MessagingLayout 
      currentConversation={currentConversation}
      onConversationSelect={handleConversationSelect}
    >
      {/* Always show ConversationList first */}
      <ConversationList
        conversations={conversations}
        currentConversation={currentConversation}
        currentUserId={currentUserId}
        onConversationSelect={(conversation) => {
          setSelectedBet(null);
          handleConversationSelect(conversation);
        }}
        onSearch={handleSearch}
        onFilter={handleFilter}
        onPendingBetSelect={(bet) => {
          // Opening a bet closes any open conversation, so the pane always has
          // exactly one owner.
          handleConversationSelect(null);
          setSelectedBet(bet as MatchRequest);
        }}
      />

      {/* Then show the bet, the conversation, or the empty state */}
      {selectedBet ? (
        <BetDetail
          bet={selectedBet}
          currentUserId={currentUserId}
          onResponded={(updated) => {
            setSelectedBet(null);
            // Calling a bet opens the conversation it just created — the whole
            // point of accepting is that you can now talk, so making the user
            // go and find the thread would be a strange place to stop.
            if (updated.status === 'ACCEPTED' && updated.conversationId) {
              setConversationToOpen(updated.conversationId);
            }
            refreshConversations();
          }}
        />
      ) : currentConversation ? (
        <ChatInterface
          conversation={currentConversation}
          conversations={conversations}
          currentUserId={currentUserId}
          onSendMessage={handleSendMessage}
          onMessageAction={(messageId, action) => {
            logger.log('Message action:', action, 'for message:', messageId);
          }}
          onConversationChanged={() => {
            // A deleted or blocked conversation should leave the list, and a
            // mute should be reflected — none of which happened before, so the
            // thread sat there looking untouched.
            refreshConversations();
            selectConversation(null);
          }}
        />
      ) : (
        <EmptyState>
          <EmptyStateIcon>💬</EmptyStateIcon>
          <EmptyStateTitle>Welcome to Messages</EmptyStateTitle>
          <EmptyStateDescription>
            Select a conversation from the sidebar to start messaging, or create a new conversation to get started.
          </EmptyStateDescription>
        </EmptyState>
      )}
    </MessagingLayout>
  );
};
